import { Yap } from '../types';

/**
 * Calculate trending score for a yap within a specific group context
 * Ranks based solely on engagement within that group
 * 
 * @param yap - The yap to calculate score for
 * @param groupEngagement - Engagement metrics from group members only
 * @returns Trending score (higher = more trending)
 */
export function calculateGroupTrendingScore(
    yap: Yap,
    groupEngagement?: { likes: number; comments: number; shouts: number }
): number {
    // Use group-specific engagement if provided, otherwise fall back to yap's total engagement
    const likes = groupEngagement?.likes ?? yap.likes ?? 0;
    const comments = groupEngagement?.comments ?? yap.comments ?? 0;
    const shouts = groupEngagement?.shouts ?? yap.shouts ?? 0;

    // Time decay factor - newer yaps get higher scores
    const now = Date.now();
    const yapTime = typeof yap.time === 'string' ? new Date(yap.time).getTime() : yap.time;
    const ageInHours = (now - yapTime) / (1000 * 60 * 60);
    const timeDecay = Math.max(0, 1 - (ageInHours / 24)); // Decay over 24 hours

    // Weighted engagement score
    // Comments worth more than likes, shouts worth most
    const engagementScore = (likes * 1) + (comments * 3) + (shouts * 5);

    // Final trending score with time decay
    return engagementScore * (0.5 + timeDecay * 0.5);
}

/**
 * Sort yaps by group-specific trending score
 * 
 * @param yaps - Array of yaps to sort
 * @param groupEngagementMap - Optional map of yapId to group-specific engagement
 * @returns Sorted array (highest trending first)
 */
export function sortByGroupTrending(
    yaps: Yap[],
    groupEngagementMap?: Map<string, { likes: number; comments: number; shouts: number }>
): Yap[] {
    return [...yaps].sort((a, b) => {
        const scoreA = calculateGroupTrendingScore(a, groupEngagementMap?.get(a.id));
        const scoreB = calculateGroupTrendingScore(b, groupEngagementMap?.get(b.id));
        return scoreB - scoreA; // Descending order
    });
}
